import React, {
    useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import {
    Box, Typography, 
    useTheme
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import LottieAnimation from './lottie';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';

type Props = {
    sectionKey: string,
    title: string,
    subtitle: string,
    user?: string,
    duration?: number,
    quote: string,
    backJump?: number
};

export const QuoteScreen = ({
    sectionKey,
    title,
    subtitle,
    user,
    duration,
    quote,
    backJump = 1
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const iconContainerRef = useRef(null);
    const textSectionRef = useRef(null);

    const initialScreenHeight = useMemo(() => {
        if (typeof window !== 'undefined') {
            return window.innerHeight;
        }
        return 0;
    }, []);
    const [screenHeight, setScreenHeight] = useState(initialScreenHeight);
    const iconContainerHeight = useMemo(() => {
        if (iconContainerRef.current) {
            return iconContainerRef.current.clientHeight;
        }
        return 0;
    }, [iconContainerRef.current]);
    const textSectionHeight = useMemo(() => {
        if (textSectionRef.current) {
            return textSectionRef.current.clientHeight;
        }
        return 0;
    }, [textSectionRef.current]);
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const resizeListener = () => {
            setScreenHeight(window.innerHeight);
        };
        window.addEventListener('resize', resizeListener);

        return () => {
            window.removeEventListener('resize', resizeListener);
        };
    }, []);

    const section = useMemo(() => {
        return formContext?.sections.find(s => s.key === sectionKey);
    }, [sectionKey, formContext?.sections]);

    const [skipAnimation, setSkipAnimation] = useState(false);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            scrollbarWidth: 'none',
            width: '100vw',
            maxWidth: '692px',
            padding: '0 24px',
            transform: 'translate(-24px, 0)',
        },
        checkmark: {
            width: '79px',
            height: '79px',
            margin: '0 auto',
            marginBottom: '32px',
            opacity: 0.4,
            position: 'absolute',
            transform: 'translate(20.5px, 20.5px)'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        iconContainer: {
            width: '80px',
            height: '80px',
            marginBottom: '32px',
            transform: 'translate(0, 66.2px)',
            animation: `$shiftUp ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 3.7}s`
        },
        icon: {
            width: '50%',
            height: '50%',
            fill: theme.palette.white,
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards 0s, $fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s`,
        },
        textSection: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
            marginBottom: '32px',
            opacity: 0,
            transform: 'translate(0, 66.2px)',
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.7}s, $shiftUp ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 3.7}s`,
        },
        title: {
            fontWeight: TypographyWeights.BOLD,
            textAlign: 'center'
        },
        subtitle: {
            textAlign: 'center'
        },
        circleTakeoverContainer: {
            overflow: 'hidden',
            width: 'calc(100% + 48px)',
            display: 'grid',
            justifyContent: 'center',
            transform: 'translate(0, 100vh)',
            height: `${screenHeight - iconContainerHeight - textSectionHeight - 152}px`,
            animation: `$slideUp ${skipAnimation ? 0 : 0.5}s ease-in forwards ${skipAnimation ? 0 : 3.7}s`
        },
        circleTakeover: {
            width: '692px',
            height: '692px',
            borderRadius: '50%',
            padding: '80px 180px 450px',
        },
        circleContent: {
            display: 'grid',
            gridTemplateColumns: '39px 1fr 39px',
            gridGap: '16px',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 4.2}s`
        },
        quoteBox: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
            alignItems: 'center'
        },
        quote: {
            fontWeight: TypographyWeights.SEMI_BOLD,
            textAlign: 'center',
            color: section?.colors.buttonArrow
        },
        name: {
            fontWeight: TypographyWeights.BOLD,
            textAlign: 'center',
            color: section?.colors.buttonArrow
        },
        state: {
            fontWeight: TypographyWeights.SEMI_BOLD,
            textAlign: 'center',
            color: section?.colors.buttonArrow
        },
        background: {
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: section?.colors.main,
            borderRadius: '50%',
            width: '80px',
            height: '80px',
            flexShrink: 0
        },
        goalsIcon: {
            width: '67.5%',
            height: '67.5%'
        },
        checkmarkContainer: {
            position: 'absolute',
            width: '130px',
            height: '130px',
            transform: 'translate(-25px, -25px)'
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes slideUp': {
            '0%': {
                transform: 'translate(0, 100vh)' 
            },
            '100%': {
                transform: 'translate(0, 0)' 
            },
        },
        '@keyframes shiftUp': {
            '0%': {
                transform: 'translate(0, 66.2px)' 
            },
            '100%': {
                transform: 'translate(0, 3px)' 
            },
        }
    })), [skipAnimation]);
    const classes = useStyles();
    const theme = useTheme();

    const onClick = () => {
        if (!skipAnimation) setSkipAnimation(true);
        else formContext.goNext();
    };

    useEffect(() => {
        setTimeout(() => {
            setSkipAnimation(true);
        }, 4700);
    }, []);

    const onBackClick = () => {
        formContext.goBack(backJump);
    };

    return (
        <CardContainer
            sectionKey={sectionKey}
            goBack={onBackClick}
            goNext={onClick}
            backgroundColor={theme.palette.white}
            hasArrowButtonBorder
            hideHeaderText
        >
            <Card>
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <Box
                            ref={iconContainerRef}
                            className={classes.iconContainer}
                        >
                            <Box>
                                <Box className={classes.background}>
                                    <Box
                                        className={`${classes.icon} ${section?.key === 'goals' ? classes.goalsIcon : ''}`}
                                        style={{
                                            fill: section?.colors.buttonArrow
                                        }}
                                    >
                                        {section?.icon}
                                    </Box>
                                </Box>
                            </Box>
                            <Box className={classes.checkmarkContainer}>
                                <LottieAnimation
                                    fileName={'checkmark.json'}
                                    segments={[{
                                        frames: [0, 90] 
                                    }]}
                                    delay={1000}
                                />
                            </Box>
                        </Box>
                        <Box
                            ref={textSectionRef}
                            className={classes.textSection}
                        >
                            <Typography
                                variant={TypographyVariants.H2}
                                className={classes.title}
                            >
                                {title}
                            </Typography>
                            <Typography
                                variant={TypographyVariants.H5}
                                className={classes.subtitle}
                            >
                                {subtitle}
                            </Typography>
                        </Box>
                        <Box className={classes.circleTakeoverContainer}>
                            <Box
                                className={classes.circleTakeover}
                                style={{
                                    backgroundColor: section?.colors.main,
                                    paddingTop: user ? '80px' : '56px' 
                                }}
                            >
                                <Box
                                    className={classes.circleContent}
                                    style={{
                                        display: user ? 'grid' : 'flex',
                                        padding: user ? 0 : '0 39px' 
                                    }}
                                >
                                    {user && (
                                        <svg
                                            width="39"
                                            height="26"
                                            viewBox="0 0 39 26"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M28.9903 26C27.1961 26 25.6223 25.5238 24.2688 24.5714C22.9153 23.6191 21.8608 22.3651 21.1053 20.8095C20.3814 19.254 20.0194 17.5556 20.0194 15.7143C20.0194 12.6032 21.0581 9.63492 23.1356 6.80953C25.2131 3.98413 27.9201 1.71429 31.2567 0L39 4.38095C36.9225 5.36508 35.0969 6.53968 33.523 7.90476C31.9492 9.26984 30.8475 10.8571 30.2179 12.6667C32.0121 12.9206 33.523 13.6032 34.7506 14.7143C36.0097 15.7937 36.6392 17.2381 36.6392 19.0476C36.6392 20.9524 35.9152 22.5873 34.4673 23.9524C33.0508 25.3175 31.2252 26 28.9903 26ZM8.97094 26C7.17676 26 5.60291 25.5238 4.24939 24.5714C2.89588 23.6191 1.8414 22.3651 1.08596 20.8095C0.361986 19.254 0 17.5556 0 15.7143C0 12.6032 1.03874 9.63492 3.11622 6.80953C5.1937 3.98413 7.90073 1.71429 11.2373 0L18.9806 4.38095C16.9031 5.36508 15.0775 6.53968 13.5036 7.90476C11.9298 9.26984 10.8281 10.8571 10.1985 12.6667C11.9927 12.9206 13.5036 13.6032 14.7312 14.7143C15.9903 15.7937 16.6199 17.2381 16.6199 19.0476C16.6199 20.9524 15.8959 22.5873 14.4479 23.9524C13.0315 25.3175 11.2058 26 8.97094 26Z"
                                                fill={section?.colors.background}
                                            />
                                        </svg>
                                    )}
                                    <Box
                                        className={classes.quoteBox}
                                        style={{
                                            rowGap: user ? '16px' : '45px' 
                                        }}
                                    >
                                        <Typography
                                            variant={TypographyVariants.H6}
                                            className={classes.quote}
                                            dangerouslySetInnerHTML={{
                                                __html: quote 
                                            }}
                                        />
                                        <Box>
                                            {user && (
                                                <Typography
                                                    variant={TypographyVariants.H6}
                                                    className={classes.name}
                                                >
                                                    {user}
                                                </Typography>
                                            )}
                                            <Typography
                                                variant={TypographyVariants.SMALL}
                                                className={classes.state}
                                            >
                                                {user ? `Sunnyside member for ${duration} ${duration > 1 ? 'years' : 'year'}` : 'Sunnyside Impact Survey, 2023'}
                                            </Typography>
                                        </Box>
                                        {user && (
                                            <svg
                                                width="132"
                                                height="20"
                                                viewBox="0 0 132 20"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M8.87919 1.9078C9.33768 0.978647 10.6626 0.978653 11.1211 1.9078L13.2792 6.28131L18.1054 6.98254C19.1307 7.13151 19.5401 8.39146 18.7982 9.11464L15.306 12.5189L16.1301 17.3254C16.3052 18.3466 15.2333 19.1253 14.3163 18.643L10.0001 16.3732L5.68399 18.643C4.76698 19.1253 3.69506 18.3466 3.87016 17.3254L4.6943 12.5189L1.20207 9.11464C0.460198 8.39146 0.869603 7.13151 1.89487 6.98254L6.72107 6.28131L8.87919 1.9078Z"
                                                    fill={section?.colors.buttonArrow}
                                                />
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M36.8792 1.9078C37.3377 0.978647 38.6626 0.978653 39.1211 1.9078L41.2792 6.28131L46.1054 6.98254C47.1307 7.13151 47.5401 8.39146 46.7982 9.11464L43.306 12.5189L44.1301 17.3254C44.3052 18.3466 43.2333 19.1253 42.3163 18.643L38.0001 16.3732L33.684 18.643C32.767 19.1253 31.6951 18.3466 31.8702 17.3254L32.6943 12.5189L29.2021 9.11464C28.4602 8.39146 28.8696 7.13151 29.8949 6.98254L34.7211 6.28131L36.8792 1.9078Z"
                                                    fill={section?.colors.buttonArrow}
                                                />
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M64.8792 1.9078C65.3377 0.978647 66.6626 0.978653 67.1211 1.9078L69.2792 6.28131L74.1054 6.98254C75.1307 7.13151 75.5401 8.39146 74.7982 9.11464L71.306 12.5189L72.1301 17.3254C72.3052 18.3466 71.2333 19.1253 70.3163 18.643L66.0001 16.3732L61.684 18.643C60.767 19.1253 59.6951 18.3466 59.8702 17.3254L60.6943 12.5189L57.2021 9.11464C56.4602 8.39146 56.8696 7.13151 57.8949 6.98254L62.7211 6.28131L64.8792 1.9078Z"
                                                    fill={section?.colors.buttonArrow}
                                                />
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M92.8792 1.9078C93.3377 0.978647 94.6626 0.978653 95.1211 1.9078L97.2792 6.28131L102.105 6.98254C103.131 7.13151 103.54 8.39146 102.798 9.11464L99.306 12.5189L100.13 17.3254C100.305 18.3466 99.2333 19.1253 98.3163 18.643L94.0001 16.3732L89.684 18.643C88.767 19.1253 87.6951 18.3466 87.8702 17.3254L88.6943 12.5189L85.2021 9.11464C84.4602 8.39146 84.8696 7.13151 85.8949 6.98254L90.7211 6.28131L92.8792 1.9078Z"
                                                    fill={section?.colors.buttonArrow}
                                                />
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M120.879 1.9078C121.338 0.978647 122.663 0.978653 123.121 1.9078L125.279 6.28131L130.105 6.98254C131.131 7.13151 131.54 8.39146 130.798 9.11464L127.306 12.5189L128.13 17.3254C128.305 18.3466 127.233 19.1253 126.316 18.643L122 16.3732L117.684 18.643C116.767 19.1253 115.695 18.3466 115.87 17.3254L116.694 12.5189L113.202 9.11464C112.46 8.39146 112.87 7.13151 113.895 6.98254L118.721 6.28131L120.879 1.9078Z"
                                                    fill={section?.colors.buttonArrow}
                                                />
                                            </svg>
                                        )}
                                    </Box>
                                    {user && (
                                        <svg
                                            width="39"
                                            height="26"
                                            viewBox="0 0 39 26"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M10.0097 4.44176e-06C11.8039 4.28491e-06 13.3777 0.476193 14.7312 1.42857C16.0847 2.38095 17.1392 3.63492 17.8947 5.19048C18.6186 6.74603 18.9806 8.44445 18.9806 10.2857C18.9806 13.3968 17.9419 16.3651 15.8644 19.1905C13.7869 22.0159 11.0799 24.2857 7.74334 26L-3.82995e-07 21.6191C2.07748 20.6349 3.90315 19.4603 5.477 18.0952C7.05085 16.7302 8.15254 15.1429 8.78208 13.3333C6.98789 13.0794 5.477 12.3968 4.2494 11.2857C2.99031 10.2064 2.36078 8.76191 2.36078 6.95238C2.36078 5.04762 3.08475 3.4127 4.53269 2.04762C5.94915 0.682542 7.77482 4.63714e-06 10.0097 4.44176e-06ZM30.0291 2.69161e-06C31.8232 2.53476e-06 33.3971 0.476191 34.7506 1.42857C36.1041 2.38095 37.1586 3.63492 37.914 5.19047C38.638 6.74603 39 8.44445 39 10.2857C39 13.3968 37.9613 16.3651 35.8838 19.1905C33.8063 22.0159 31.0993 24.2857 27.7627 26L20.0194 21.619C22.0969 20.6349 23.9225 19.4603 25.4964 18.0952C27.0702 16.7302 28.1719 15.1429 28.8015 13.3333C27.0073 13.0794 25.4964 12.3968 24.2688 11.2857C23.0097 10.2064 22.3801 8.76191 22.3801 6.95238C22.3801 5.04762 23.1041 3.4127 24.5521 2.04762C25.9685 0.68254 27.7942 2.88699e-06 30.0291 2.69161e-06Z"
                                                fill={section?.colors.background}
                                            />
                                        </svg>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box
                        style={{
                            height: '300px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default QuoteScreen;
